import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { catchError, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { RegistrationService } from '../service';
import * as fromRegistrationActions from './registration.actions';
import { Action } from '@ngrx/store';
import { Router } from '@angular/router';

@Injectable()
export class RegistrationEffects {
  initiateRegistration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromRegistrationActions.initiateRegistration),
      switchMap(({ contractnumber, initialPassphrase }) =>
        this.registrationService.register({ contractnumber, initialPassphrase }).pipe(
          map((registration) => fromRegistrationActions.initiateRegistrationSuccess({ registration })),
          catchError(() =>
            of(
              fromRegistrationActions.initiateRegistrationFailure({
                registrationResult: { isServerError: true, isCanceled: false },
                error: true
              })
            )
          )
        )
      )
    )
  );

  // eslint-disable-next-line @typescript-eslint/member-ordering
  navigateToRegistrationQr$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromRegistrationActions.initiateRegistrationSuccess),
        tap(() => this.router.navigate(['/registration-qr']))
      ),
    { dispatch: false }
  );

  // eslint-disable-next-line @typescript-eslint/member-ordering
  pollRegistrationStatus$ = createEffect(() => {
    const stopPolling$ = this.actions$.pipe(ofType(fromRegistrationActions.cancelRegistration));

    return this.actions$.pipe(
      ofType(fromRegistrationActions.initiateRegistrationSuccess),
      switchMap(({ registration }) =>
        this.registrationService.pollRegistrationStatus(registration).pipe(
          takeUntil(stopPolling$),
          filter((result) => !result.isPending),
          map((result): Action =>
            result.isRegistered
              ? fromRegistrationActions.registrationAccepted()
              : fromRegistrationActions.registrationRejected({ registrationResult: result, error: true })
          ),
          catchError(() =>
            of(
              fromRegistrationActions.registrationRejected({
                registrationResult: {
                  ...registration,
                  isPending: false,
                  isRegistered: false,
                  isCanceled: true,
                  isServerError: true
                },
                error: true
              })
            )
          )
        )
      )
    );
  });

  // eslint-disable-next-line @typescript-eslint/member-ordering
  registrationAccepted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromRegistrationActions.registrationAccepted),
      tap(() => this.router.navigate(['/pages/login'])),
      map(() => fromRegistrationActions.resetRegistration())
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly registrationService: RegistrationService,
    private readonly router: Router
  ) {}
}
